import {distance} from 'fastest-levenshtein'

/**
 * Maximum allowed string length for similarity comparison
 * Prevents DoS attacks via extremely long inputs
 */
const MAX_STRING_LENGTH = 1000

/**
 * Calculate similarity between two strings using Levenshtein distance
 * Returns a value between 0 (completely different) and 1 (identical)
 *
 * @param str1 - First string to compare
 * @param str2 - Second string to compare
 * @returns Similarity score from 0 to 1, or -1 if invalid input
 */
export const calculateStringSimilarity = (str1: string, str2: string): number => {
  // Return -1 if either string is empty to fail fraud detection checks
  if (!str1 || !str2) return -1

  // Prevent DoS attacks from extremely long inputs
  if (str1.length > MAX_STRING_LENGTH || str2.length > MAX_STRING_LENGTH) return -1

  const maxLen = Math.max(str1.length, str2.length)
  if (maxLen === 0) return 1

  const dist = distance(str1, str2)
  return 1 - dist / maxLen
}

/**
 * Check if API address matches OCR address, considering substring matches
 * This handles cases where OCR captures more detail (e.g., "5800BRISTOLPARKWAY")
 * than the API (e.g., "***BRISTOLPARKWAY" -> "BRISTOLPARKWAY")
 *
 * @param apiAddress - Normalized address from API
 * @param ocrAddress - Normalized address from OCR
 * @param threshold - Similarity threshold (0-1)
 * @returns true if addresses match (either by similarity or substring)
 */
export const isAddressMatch = (
  apiAddress: string,
  ocrAddress: string,
  threshold: number
): boolean => {
  if (!apiAddress || !ocrAddress) return false

  // Check if API address is contained within OCR address
  // This handles cases like OCR="5800BRISTOLPARKWAY" and API="BRISTOLPARKWAY"
  if (ocrAddress.includes(apiAddress)) {
    return true
  }

  // Check if OCR address is contained within API address
  if (apiAddress.includes(ocrAddress)) {
    return true
  }

  // Fall back to similarity calculation
  const similarity = calculateStringSimilarity(apiAddress, ocrAddress)
  return similarity >= threshold
}

/**
 * Normalize text for comparison by removing all non-alphanumeric characters
 * and converting to lowercase
 *
 * @param text - Text to normalize
 * @returns Normalized text containing only lowercase letters and numbers
 */
export const normalizeForComparison = (text: string): string => {
  return text.toLowerCase().replace(/[^a-z0-9]/g, '')
}

/**
 * Remove leading street numbers from address for comparison
 *
 * @param text - Address text to process
 * @returns Address without leading numbers
 */
export const removeStreetNumber = (text: string): string => {
  return text.replace(/^\d+\s*/, '')
}
